// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { useCallback, useMemo, useState } from 'react';
import { InstrumentKey } from '../../../utils/instrument';
import { CollateralAgreementRow } from '../obligations/types';

export const useRequestCollateralAgreementForm = (
  row: CollateralAgreementRow | undefined,
  party: string,
  allAgreementIds: string[],
  initialSelectedInstruments: InstrumentKey[],
) => {
  // --- AGREEMENT ID ---
  const [agreementId, setAgreementId] = useState<string>(row?.payload.id || '');
  const [agreementIdValidationMsg, setAgreementIdValidationMsg] = useState<string>('');

  const validateAgreementId = useCallback(
    (value: string) => {
      if (allAgreementIds?.includes(value)) {
        return 'This agreement Id exists already. Please ensure the agreement Ids are unique.';
      }
      return '';
    },
    [allAgreementIds],
  );

  // --- COUNTERPARTY ---
  const [counterparty, setCounterparty] = useState<string>(row?.payload.counterparty || '');
  const [counterpartyValidationMsg, setCounterpartyValidationMsg] = React.useState<string>('');

  const validateCounterParty = useCallback(
    (value: string) => {
      if (value === party) {
        return 'Counterparty cannot be your own party ID';
      }
      return '';
    },
    [party],
  );

  // --- INSTRUMENTS ---
  const [selectedInstruments, setSelectedInstruments] = React.useState<Set<InstrumentKey>>(
    new Set(initialSelectedInstruments),
  );

  // --- MINIMUM TRANSFER AMOUNT ---
  const [minimumTransferAmount, setMinimumTransferAmount] = useState<string>(
    row?.payload.minimumTransferAmount ?? '',
  );
  const [minimumTransferAmountValidationMsg, setMinimumTransferAmountValidationMsg] =
    useState<string>('');

  const validateMinimumTransferAmount = (value: string) => {
    const numericValue = parseFloat(value);

    if (Number.isFinite(numericValue) && numericValue < 0) {
      return 'Minimum Transfer Amount cannot be negative.';
    }

    return '';
  };

  // --- UTILS ---
  const isSubmitDisabled = useMemo(() => {
    const hasSelectedInstrumentsChange =
      JSON.stringify(selectedInstruments) !== JSON.stringify(initialSelectedInstruments);

    if (row && !hasSelectedInstrumentsChange) {
      return false;
    }

    return !(
      agreementId &&
      !agreementIdValidationMsg &&
      counterparty &&
      !counterpartyValidationMsg &&
      selectedInstruments.size > 0 &&
      minimumTransferAmount &&
      !minimumTransferAmountValidationMsg
    );
  }, [
    row,
    agreementId,
    agreementIdValidationMsg,
    counterparty,
    counterpartyValidationMsg,
    selectedInstruments,
    minimumTransferAmount,
    minimumTransferAmountValidationMsg,
    initialSelectedInstruments,
  ]);

  return {
    agreementId,
    setAgreementId,
    agreementIdValidationMsg,
    setAgreementIdValidationMsg,
    validateAgreementId,

    counterparty,
    setCounterparty,
    counterpartyValidationMsg,
    setCounterpartyValidationMsg,
    validateCounterParty,

    selectedInstruments,
    setSelectedInstruments,

    minimumTransferAmount,
    setMinimumTransferAmount,
    minimumTransferAmountValidationMsg,
    setMinimumTransferAmountValidationMsg,
    validateMinimumTransferAmount,

    isSubmitDisabled,
  };
};
